"use client";

import React from "react";
import { Loader, Send, CircleCheck, CircleAlert, Zap, Headphones, CheckCircle2, Cat, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function FreeTrialFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="222234" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mb-6 shadow-sm">
          <CircleCheck className="size-10" />
        </div>
        <h3 className="text-3xl font-default font-bold text-slate-900 mb-3 tracking-tight">
          Welcome to the Pride
        </h3>
        <p className="text-slate-600 mb-8 max-w-md text-lg leading-relaxed">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" size="lg" className="rounded-xl border-primary text-primary hover:bg-primary/5 px-8 font-bold">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-900 font-semibold ml-1">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Leo Smith" className="rounded-xl border-slate-200 focus-visible:ring-primary/20 bg-slate-50/50 h-12 px-4 transition-all" {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-900 font-semibold ml-1">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="leo@werkys.com" className="rounded-xl border-slate-200 focus-visible:ring-primary/20 bg-slate-50/50 h-12 px-4 transition-all" {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-900 font-semibold ml-1">Business Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="rounded-xl border-slate-200 focus-visible:ring-primary/20 bg-slate-50/50 h-12 px-4 transition-all" {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />

        <FormField control={control} name="program" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-900 font-semibold ml-1">Plan Interest</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full rounded-xl border-slate-200 focus:ring-primary/20 h-12 bg-slate-50/50 px-4">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-xl shadow-lg border-slate-200">
                  {["Starter Plan", "Growth Plan", "Scale Plan", "Enterprise Custom"].map((program, i) => <SelectItem key={program} value={program} data-index={i} className="rounded-lg my-1 mx-1">
                      {program}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="ml-1" />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_notes" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-900 font-semibold ml-1">Special Requirements (Optional)</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y transition-all" placeholder="Any specific AI integration needs or custom requests?" {...field} />
            </FormControl>
            <FormMessage className="ml-1" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-1 duration-300">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 bg-primary text-white hover:bg-primary/90 rounded-xl active:scale-[0.98]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Initializing...
          </> : <>
            Start Your Free Trial
            <Send className="ml-2 size-5" />
          </>}
      </Button>
      
      <p className="text-center text-[10px] uppercase tracking-widest text-slate-400 mt-6 font-bold">
        Secure & Private • No Credit Card Required
      </p>

      <Button variant="outline" className="w-full mt-4 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold">
        Learn More
      </Button>
    </Form>;
}
export default function BeginYourFreeTrial() {
  const benefits = [{
    id: 1,
    title: "Instant Deployment",
    desc: "Get your environment live in under 60 seconds.",
    icon: Zap
  }, {
    id: 2,
    title: "Cat-Like Reflexes",
    desc: "Ultra-low latency AI processing for peak performance.",
    icon: Cat
  }, {
    id: 3,
    title: "Expert Support",
    desc: "Access to our dedicated pride of AI engineers 24/7.",
    icon: Headphones
  }];
  return <section id="join-the-pride" className="relative py-24 lg:py-32 overflow-hidden bg-primary">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636683/img/paper_texture.png)] bg-cover bg-center opacity-[0.03]" />
        <div className="absolute top-1/4 -left-24 w-96 h-96 bg-white/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 -right-24 w-96 h-96 bg-black/5 blur-[120px] rounded-full" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div className="text-white animate-in fade-in slide-in-from-left-8 duration-700">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-mono font-bold tracking-[0.2em] mb-8 uppercase">
              <Sparkles className="size-3 text-white animate-pulse" />
              Join the Pride
            </div>
            
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-default font-bold mb-8 tracking-tighter leading-[1.1]">
              Ready to <br className="hidden md:block" />
              Scale Your <span className="italic underline decoration-white/30 decoration-4 underline-offset-8">Output?</span>
            </h2>
            
            <p className="text-xl text-white/90 mb-12 max-w-xl font-default leading-relaxed">Experience the speed of Werkys with a 7-day free evaluation. Deploy your high-performance AI platform with cat-like reflexes today.</p>

            <div className="space-y-10">
              {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="group flex items-start gap-6 transition-transform duration-300 hover:translate-x-2">
                  <div className="size-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/10 shadow-sm transition-all group-hover:bg-white/20 group-hover:scale-110">
                    <benefit.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white mb-1 tracking-tight">{benefit.title}</h4>
                    <p className="text-white/70 text-lg leading-snug">{benefit.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="relative animate-in fade-in slide-in-from-right-8 duration-700 delay-150">
            {}
            <div className="bg-white rounded-3xl p-8 md:p-10 lg:p-12 relative overflow-hidden flex flex-col items-center justify-center text-center">
              {}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-linear-to-r from-transparent via-primary/40 to-transparent" />

              <div className="mb-10">
                <h3 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Start Your Trial</h3>
                
              </div>
              <div onClick={() => window.location.href = "https://account.werkys.com/sign-up"} style={{
              display: "inline-block",
              width: "70%",
              backgroundColor: "orange",
              color: "white",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "6px",
              cursor: "pointer",
              userSelect: "none",
              borderRadius: "1.75rem"
            }}>
                Start Your Free Trial
              </div>
            </div>

            {}
            <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full bg-black/5 rounded-3xl blur-3xl opacity-40" />
          </div>
        </div>
      </div>
    </section>;
}